import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { AiImageVersionsGallery } from "./AiImageVersionsGallery";
import {
  getAiAssetsForEntity,
  getMediaAssetsForEntity,
  uploadMedia,
  removeBackground,
  enhanceImage,
  generatePoster,
} from "../aiApi";
import { ListingEntityType, AiAsset } from "./types/ai.types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  entityType: ListingEntityType;
  entityId: string;
  title?: string;
  description?: string;
  price?: number;
  location?: string;
  initialFields?: Record<string, unknown>;
  onApplyImage?: (asset: AiAsset) => void;
};

export const AiImageStudioDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  title,
  description,
  price,
  location,
  initialFields,
  onApplyImage,
}) => {
  const toast = useToast();

  const [assets, setAssets] = useState<AiAsset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<AiAsset | null>(null);
  const [provider, setProvider] = useState<"openai" | "none">("openai");
  const [customPrompt, setCustomPrompt] = useState("");
  const [loading, setLoading] = useState(false);

  const drawerBg = useColorModeValue("white", "gray.900");
  const sectionBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.800", "white");
  const bodyTextColor = useColorModeValue("gray.800", "whiteAlpha.900");

  const inputBg = useColorModeValue("white", "gray.800");
  const inputTextColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const inputBorderColor = useColorModeValue("gray.200", "gray.600");
  const inputHoverBorderColor = useColorModeValue("teal.400", "teal.300");
  const inputFocusBorderColor = useColorModeValue("teal.500", "teal.300");
  const inputInvalidBorderColor = useColorModeValue("red.400", "red.300");
  const inputDisabledBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const placeholderColor = useColorModeValue("gray.400", "gray.500");

  const selectOptionBg = useColorModeValue("white", "gray.800");
  const selectOptionColor = useColorModeValue("gray.800", "whiteAlpha.900");

  const closeButtonHoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const ghostButtonHoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const drawerShadow = useColorModeValue("xl", "dark-lg");

  const fileButtonBg = useColorModeValue("gray.100", "whiteAlpha.200");
  const fileButtonHoverBg = useColorModeValue("gray.200", "whiteAlpha.300");

  const selectedAssetId = useMemo(
    () => selectedAsset?._id || selectedAsset?.id,
    [selectedAsset],
  );

  const sharedInputStyles = {
    bg: inputBg,
    color: inputTextColor,
    borderColor: inputBorderColor,
    borderRadius: "xl",
    transition: "all 0.18s ease",
    _placeholder: {
      color: placeholderColor,
    },
    _hover: {
      borderColor: inputHoverBorderColor,
    },
    _focus: {
      borderColor: inputFocusBorderColor,
      boxShadow: `0 0 0 1px ${inputFocusBorderColor}`,
    },
    _focusVisible: {
      borderColor: inputFocusBorderColor,
      boxShadow: `0 0 0 1px ${inputFocusBorderColor}`,
    },
    _invalid: {
      borderColor: inputInvalidBorderColor,
      boxShadow: `0 0 0 1px ${inputInvalidBorderColor}`,
    },
    _disabled: {
      bg: inputDisabledBg,
      opacity: 0.75,
      cursor: "not-allowed",
    },
    sx: {
      "&:-webkit-autofill": {
        boxShadow: `0 0 0px 1000px ${inputBg} inset`,
        WebkitTextFillColor: inputTextColor,
        caretColor: inputTextColor,
        transition: "background-color 9999s ease-in-out 0s",
      },
      "& option": {
        background: selectOptionBg,
        color: selectOptionColor,
      },
      "&::file-selector-button": {
        border: "0",
        borderRadius: "10px",
        padding: "6px 10px",
        marginRight: "10px",
        cursor: "pointer",
        background: fileButtonBg,
        color: inputTextColor,
        transition: "all 0.18s ease",
      },
      "&::file-selector-button:hover": {
        background: fileButtonHoverBg,
      },
    },
  };

  const loadAssets = async () => {
    setLoading(true);

    try {
      const [aiRows, mediaRows] = await Promise.all([
        getAiAssetsForEntity(entityType, entityId).catch(() => []),
        getMediaAssetsForEntity(entityType, entityId).catch(() => []),
      ]);

      const merged = [...aiRows, ...mediaRows];
      const unique = Array.from(
        new Map(
          merged.map((asset) => [
            asset._id || asset.id || asset.url,
            asset,
          ]),
        ).values(),
      );

      setAssets(unique);

      if (!selectedAsset && unique[0]) {
        setSelectedAsset(unique[0]);
      }
    } catch (error: any) {
      toast({
        title: "Failed to load image assets",
        description: error?.message || "Please check backend logs.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    setLoading(true);

    try {
      const uploaded = await uploadMedia({
        entityType,
        entityId,
        file,
        kind: "original",
      });

      setAssets((prev) => [uploaded, ...prev]);
      setSelectedAsset(uploaded);

      toast({
        title: "Image uploaded",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error?.message || "Please try again.",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      event.target.value = "";
    }
  };

  const requireSelected = (): AiAsset | null => {
    if (!selectedAsset) {
      toast({
        title: "Select an image first",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return null;
    }

    return selectedAsset;
  };

  const runImageAction = async (
    action: "remove_background" | "enhance" | "poster",
  ) => {
    const asset = requireSelected();

    if (!asset) return;

    setLoading(true);

    try {
      const payload = {
        entityType,
        entityId,
        provider,
        assetId: asset._id || asset.id,
        imageUrl: asset.url,
        customPrompt,
        title,
        description,
        price,
        location,
        fields: initialFields,
      };

      const generated =
        action === "remove_background"
          ? await removeBackground(payload)
          : action === "enhance"
            ? await enhanceImage(payload)
            : await generatePoster(payload);

      setAssets((prev) => [generated, ...prev]);
      setSelectedAsset(generated);

      toast({
        title:
          action === "remove_background"
            ? "Background removed"
            : action === "enhance"
              ? "Image enhanced"
              : "Poster generated",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Image action failed",
        description: error?.message || "Please check backend logs.",
        status: "error",
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadAssets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entityType, entityId]);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="xl">
      <DrawerOverlay bg="blackAlpha.600" />

      <DrawerContent
        bg={drawerBg}
        color={bodyTextColor}
        boxShadow={drawerShadow}
        borderLeftWidth="1px"
        borderColor={borderColor}
      >
        <DrawerCloseButton
          color={mutedText}
          _hover={{ bg: closeButtonHoverBg, color: headingColor }}
        />

        <DrawerHeader borderBottomWidth="1px" borderColor={borderColor}>
          <Box>
            <HStack spacing={2}>
              <Text color={headingColor}>🖼️ AI Image Studio</Text>
              <Badge colorScheme="teal" borderRadius="full">
                {entityType}
              </Badge>
            </HStack>

            <Text fontSize="sm" color={mutedText} fontWeight="normal" mt={1}>
              Upload, clean, enhance and generate social-ready PickDengu images.
            </Text>
          </Box>
        </DrawerHeader>

        <DrawerBody py={5}>
          <VStack align="stretch" spacing={5}>
            <Box
              p={4}
              bg={sectionBg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="2xl"
            >
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm" color={headingColor}>
                    Upload image
                  </FormLabel>

                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleUpload}
                    p={1}
                    {...sharedInputStyles}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" color={headingColor}>
                    Provider
                  </FormLabel>

                  <Select
                    value={provider}
                    onChange={(event) =>
                      setProvider(event.target.value as "openai" | "none")
                    }
                    {...sharedInputStyles}
                  >
                    <option value="openai">OpenAI</option>
                    <option value="none">No AI / Sharp fallback</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              <FormControl mt={4}>
                <FormLabel fontSize="sm" color={headingColor}>
                  Custom image instructions
                </FormLabel>

                <Textarea
                  value={customPrompt}
                  onChange={(event) => setCustomPrompt(event.target.value)}
                  placeholder="Example: create a premium ecommerce poster, keep the product realistic, add clean lighting, do not change condition."
                  minH="90px"
                  resize="vertical"
                  {...sharedInputStyles}
                />
              </FormControl>

              <HStack mt={4} spacing={3} flexWrap="wrap">
                <Button
                  colorScheme="teal"
                  variant="solid"
                  size="sm"
                  onClick={() => runImageAction("remove_background")}
                  isLoading={loading}
                >
                  Remove background
                </Button>

                <Button
                  colorScheme="purple"
                  variant="outline"
                  size="sm"
                  onClick={() => runImageAction("enhance")}
                  isLoading={loading}
                >
                  Enhance
                </Button>

                <Button
                  colorScheme="orange"
                  variant="outline"
                  size="sm"
                  onClick={() => runImageAction("poster")}
                  isLoading={loading}
                >
                  Generate poster
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadAssets}
                  isLoading={loading}
                  color={bodyTextColor}
                  _hover={{ bg: ghostButtonHoverBg }}
                >
                  Refresh
                </Button>
              </HStack>
            </Box>

            <Divider borderColor={borderColor} />

            <AiImageVersionsGallery
              assets={assets}
              selectedAssetId={selectedAssetId}
              onSelect={setSelectedAsset}
              onApply={onApplyImage}
            />
          </VStack>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor={borderColor}>
          <HStack spacing={3}>
            <Button
              variant="ghost"
              onClick={onClose}
              color={bodyTextColor}
              _hover={{ bg: ghostButtonHoverBg }}
            >
              Close
            </Button>

            <Button
              colorScheme="teal"
              isDisabled={!selectedAsset}
              onClick={() => selectedAsset && onApplyImage?.(selectedAsset)}
            >
              Apply selected image
            </Button>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};